import {
  AutoComplete,
  ReadOnly,
  useEntityPermission,
  useDataProvider,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { useEffect, useState, forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';

const PurchasedFromLocation = forwardRef(
  ({ overwriteProps = {}, field, onChange, ...props }, ref) => {
    const nameKey = 'locationName';
    const valueKey = 'id';

    const provider =
      process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
    const { get, getApiUrl } = useDataProvider(provider);
    const [defaultOptions, setDefaultOptions] = useState([]);

    const { setValue, getValues, watch } = useFormContext();

    const currentValue = getValues(field?.name);
    const lastPurchasedFrom = getValues(
      field?.dependencies?.purchasedFromId || 'undefined',
    );

    const [lastPurchasedFromValue, setLastPurchasedFromValue] =
      useState(lastPurchasedFrom);

    const purchasedFrom = watch(
      field?.dependencies?.purchasedFromId || 'undefined',
    );

    const getPurchasedFromLocations = async ({
      purchasedFromId,
      searchValue,
    }) => {
      const searchParamm = searchValue
        ? `&locationName=$like:${searchValue}`
        : '';
      const result = await get(
        `${getApiUrl()}/sxcl/v1/service-providers/${purchasedFromId}/locations/?status=$eq:ACTIVE&limit=10${searchParamm}`,
      );
      if (result.ok) {
        const purchasedFromLocationsResult =
          result?.data?.data?.map(item => ({
            label: item[nameKey],
            value: item[valueKey],
          })) || [];
        return purchasedFromLocationsResult;
      }
      return null;
    };

    const loadOptions = async (searchValue, callback) => {
      if (!searchValue) return [];
      const resultsData = await getPurchasedFromLocations({
        purchasedFromId: purchasedFrom?.value,
        searchValue,
      });
      if (resultsData) {
        return callback(resultsData);
      }
      return callback([]);
    };

    const initialLoad = async purchasedFromId => {
      const result = await getPurchasedFromLocations({ purchasedFromId });

      if (currentValue?.id || currentValue?.value) {
        let currentObj = {};
        if (currentValue?.id) {
          currentObj.label = currentValue?.[nameKey];
          currentObj.value = currentValue?.[valueKey];
        }
        if (currentValue?.value) {
          currentObj = currentValue;
        }
        setDefaultOptions([
          currentObj,
          ...(result
            ? result.filter(item => item.value !== currentObj?.value)
            : []),
        ]);
      } else {
        setDefaultOptions(result);
      }
    };

    const { isHidden, isReadOnly } = useEntityPermission();

    if (isReadOnly(props?.permission, currentValue)) {
      return (
        <ReadOnly name={props?.name}>
          {currentValue?.label || currentValue?.[nameKey]}
        </ReadOnly>
      );
    }

    if (isHidden(props?.permission)) {
      return (
        <input
          type="hidden"
          name={props?.name}
          value={currentValue?.value || ''}
        />
      );
    }

    useEffect(() => {
      if (lastPurchasedFromValue?.value) {
        initialLoad(lastPurchasedFromValue?.value);
      }
    }, [lastPurchasedFromValue]);

    useEffect(() => {
      if (purchasedFrom?.value !== lastPurchasedFromValue?.value) {
        setValue(field.name, { label: '', value: '' });
        setDefaultOptions([]);
        setLastPurchasedFromValue(purchasedFrom);
      }
    }, [JSON.stringify(purchasedFrom)]);

    return (
      <AutoComplete
        ref={ref}
        defaultOptions={defaultOptions}
        loadOptions={loadOptions}
        {...props}
        overwriteProps={overwriteProps}
        isClearable
      />
    );
  },
);

export default observer(PurchasedFromLocation);
